import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import type { BufferedChildContext } from '../../modules/SettingsDetailPage';
import {
  BIREFRINGENCE_OPTIONS, ELONGATION_OPTIONS, EXTINCTION_OPTIONS, MORPHOLOGY_OPTIONS,
  OPTICAL_CHARACTER_OPTIONS, PLM_REF_LIB_BASE, RANGE_RULE_HINT, YES_NO,
  birefringenceLabel, blankType, effectiveRange, meanRiText, nextTypeName, pairInverted,
  rangeText, ruleRange, toTypePayload, toTypeRow, validateTypes, withRangeMode,
  type PlmReferenceTypeRow,
} from './plmReferenceLibrary';

interface Props extends BufferedChildContext {
  /** The asbestos names a type row may take (the PLM Result Entry names). */
  typeOptions: readonly string[];
  /** Opened from the list's "+ Add asbestos type": start with a new row. */
  addTypeOnOpen?: boolean;
}

/**
 * The "Asbestos types" section of a reference slide's detail (prototype
 * Reference-Library-v7): one row per type — Type · % · Acceptable range
 * (Rule / Override) · RI α low–high · RI γ low–high — with the optics of each
 * type behind its row's "+" (or its Optics link).
 *
 * It is a BUFFERED child of the shared detail page: nothing is written here.
 * The page's Save validates these rows first and sends them inside the slide's
 * own POST / PUT, so the slide and its types are one request and one
 * transaction. The grid is the shared `<AppDataGrid>` (its master–detail opt-in
 * carries the optics); the boxes are the shared detail-page controls.
 *
 * A row with NO types is not an error: a slide without asbestos is an ND
 * reference.
 */
export function PlmReferenceTypesEditor({
  id, viewOnly, reloadToken, handleRef, onDirty, typeOptions, addTypeOnOpen,
}: Props) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<PlmReferenceTypeRow[]>([]);
  const [optics, setOptics] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(false);
  // Save reads the rows through a ref, so the handle never holds a stale list.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const addedOnOpen = useRef(false);

  // ── load: the saved types of the record, or one empty row for a new slide ──
  useEffect(() => {
    let alive = true;
    setOptics(new Set());
    if (id === null) {
      setRows([blankType(typeOptions[0] ?? '')]);
      return;
    }
    setLoading(true);
    api.get<{ data?: { types?: Record<string, unknown>[] } }>(`${PLM_REF_LIB_BASE}/samples/${id}`)
      .then((res) => {
        if (!alive) return;
        let next = (res.data?.data?.types ?? []).map(toTypeRow);
        if (addTypeOnOpen && !addedOnOpen.current) {
          addedOnOpen.current = true;
          next = [...next, blankType(nextTypeName(next, typeOptions))];
          onDirty();
          notify.info('New asbestos type row added — fill in the %, then Save.');
        }
        setRows(next);
      })
      .catch(() => { if (alive) notify.error('Could not read the asbestos types.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // The record (or a save of it) is what re-reads the rows; the option list
    // arriving later must not throw away what the user typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reloadToken]);

  // ── the handle the shared Save calls ──────────────────────────────────────
  useEffect(() => {
    handleRef.current = {
      validate: () => {
        const errors = validateTypes(rowsRef.current);
        return errors.length ? errors.join('\n') : null;
      },
      payload: () => ({ types: rowsRef.current.map(toTypePayload) }),
    };
    return () => { handleRef.current = null; };
  }, [handleRef]);

  // ── edits ─────────────────────────────────────────────────────────────────
  const edit = (next: (rows: PlmReferenceTypeRow[]) => PlmReferenceTypeRow[]) => {
    setRows(next);
    onDirty();
  };
  const patch = (key: string, p: Partial<PlmReferenceTypeRow>) =>
    edit((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  const addType = () => edit((rs) => [...rs, blankType(nextTypeName(rs, typeOptions))]);

  const removeType = async (row: PlmReferenceTypeRow) => {
    const ok = await confirm({
      title: 'Remove asbestos type',
      message: `Remove ${row.asbestosType || 'this type'} from this reference? (Takes effect when you Save.)`,
      tone: 'danger',
      confirmLabel: 'Remove',
    });
    if (!ok) return;
    edit((rs) => rs.filter((r) => r.key !== row.key));
  };

  const toggleOptics = (key: string) =>
    setOptics((open) => {
      const next = new Set(open);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

  // A stored name that is no longer offered still shows (and can be kept).
  const optionsFor = (current: string) =>
    current && !typeOptions.some((o) => o.toLowerCase() === current.toLowerCase())
      ? [current, ...typeOptions]
      : typeOptions;

  // ── the grid ──────────────────────────────────────────────────────────────
  const columns: AppColumn<PlmReferenceTypeRow>[] = useMemo(() => {
    const box = (
      row: PlmReferenceTypeRow, field: keyof PlmReferenceTypeRow, label: string,
      opts: { required?: boolean; bad?: boolean } = {},
    ) => (
      <input
        className={`sd-input plm-reftypes-num${opts.required && !String(row[field]).trim() ? ' required-empty' : ''}${opts.bad ? ' plm-reftypes-bad' : ''}`}
        inputMode="decimal"
        aria-label={`${row.asbestosType || 'Type'} ${label}`}
        value={String(row[field])}
        disabled={viewOnly}
        onChange={(e) => patch(row.key, { [field]: e.target.value } as Partial<PlmReferenceTypeRow>)}
      />
    );
    const dash = <span className="plm-reftypes-dash">–</span>;

    const cols: AppColumn<PlmReferenceTypeRow>[] = [
      {
        field: 'asbestosType', header: 'Asbestos type', width: 180, noClip: true, sortable: false, filterable: false,
        text: (r) => r.asbestosType,
        render: (r) => (
          <select
            className={`sd-input plm-reftypes-type${r.asbestosType ? '' : ' required-empty'}`}
            aria-label="Asbestos type"
            value={r.asbestosType}
            disabled={viewOnly}
            onChange={(e) => patch(r.key, { asbestosType: e.target.value })}
          >
            {!r.asbestosType && <option value="">— Select —</option>}
            {optionsFor(r.asbestosType).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ),
      },
      {
        field: 'quantity', header: '%', width: 90, align: 'right', noClip: true, sortable: false, filterable: false,
        text: (r) => r.quantity,
        render: (r) => box(r, 'quantity', '%', { required: true }),
      },
      {
        field: 'rangeMode', header: 'Acceptable range', width: 320, noClip: true, sortable: false, filterable: false,
        text: (r) => `${rangeText(effectiveRange(r))} (${r.rangeMode.toLowerCase()})`,
        render: (r) => {
          const ovr = r.rangeMode === 'Override';
          return (
            <span className="plm-reftypes-range">
              <span className="plm-seg plm-seg--sm" role="group" aria-label={`${r.asbestosType || 'Type'} acceptable range`}>
                <button type="button" className={ovr ? '' : 'on'} aria-pressed={!ovr} disabled={viewOnly}
                  title={RANGE_RULE_HINT} onClick={() => patch(r.key, withRangeMode(r, 'Rule'))}>
                  Rule
                </button>
                <button type="button" className={ovr ? 'on' : ''} aria-pressed={ovr} disabled={viewOnly}
                  title="A certified range, e.g. from the certificate" onClick={() => patch(r.key, withRangeMode(r, 'Override'))}>
                  Override
                </button>
              </span>
              {ovr ? (
                <>
                  {box(r, 'overrideLow', 'override low', { required: true })}
                  {dash}
                  {box(r, 'overrideHigh', 'override high', { required: true })}
                  <span className="plm-reftypes-unit">%</span>
                </>
              ) : (
                <span className="plm-reftypes-rule">{rangeText(ruleRange(r.quantity))}</span>
              )}
            </span>
          );
        },
      },
      {
        field: 'riAlphaLow', header: 'RI α (low – high)', width: 190, noClip: true, sortable: false, filterable: false,
        text: (r) => (r.riAlphaLow || r.riAlphaHigh ? `${r.riAlphaLow || '—'}–${r.riAlphaHigh || '—'}` : ''),
        render: (r) => (
          <span className="plm-reftypes-pair">
            {box(r, 'riAlphaLow', 'RI α low', { bad: pairInverted(r.riAlphaLow, r.riAlphaHigh) })}
            {dash}
            {box(r, 'riAlphaHigh', 'RI α high')}
          </span>
        ),
      },
      {
        field: 'riGammaLow', header: 'RI γ (low – high)', width: 190, noClip: true, sortable: false, filterable: false,
        text: (r) => (r.riGammaLow || r.riGammaHigh ? `${r.riGammaLow || '—'}–${r.riGammaHigh || '—'}` : ''),
        render: (r) => (
          <span className="plm-reftypes-pair">
            {box(r, 'riGammaLow', 'RI γ low', { bad: pairInverted(r.riGammaLow, r.riGammaHigh) })}
            {dash}
            {box(r, 'riGammaHigh', 'RI γ high')}
          </span>
        ),
      },
      {
        field: 'birefringence', header: 'Optics', width: 110, sortable: false, filterable: false,
        // Export carries what the link hides: the optics of the type, as one line.
        text: (r) => [
          r.opticalCharacter, r.birefringence && birefringenceLabel(r.birefringence), r.pleochroism && `Pleochroism ${r.pleochroism}`,
          r.morphology, r.extinction, r.signOfElongation, meanRiText(r),
        ].filter(Boolean).join(' · '),
        render: (r) => (
          <button type="button" className="plm-reftypes-link" onClick={() => toggleOptics(r.key)}>
            {optics.has(r.key) ? 'Hide optics' : 'Optics'}
          </button>
        ),
      },
    ];
    if (!viewOnly) {
      cols.push({
        field: 'key', header: '', width: 44, align: 'center', sortable: false, filterable: false, noExport: true,
        render: (r) => (
          <button type="button" className="plm-reftypes-remove" title="Remove this type"
            aria-label={`Remove ${r.asbestosType || 'this type'}`} onClick={() => { void removeType(r); }}>
            ✕
          </button>
        ),
      });
    }
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewOnly, typeOptions, optics]);

  /** The optics of one type — the panel under its row. */
  const opticsPanel = (r: PlmReferenceTypeRow) => {
    const pick = (
      field: keyof PlmReferenceTypeRow, label: string, options: readonly string[],
      text: (v: string) => string = (v) => v,
    ) => (
      <label className="plm-reftypes-optic">
        <span>{label}</span>
        <select className="sd-input" value={String(r[field])} disabled={viewOnly}
          onChange={(e) => patch(r.key, { [field]: e.target.value } as Partial<PlmReferenceTypeRow>)}>
          <option value="">—</option>
          {options.map((o) => <option key={o} value={o}>{text(o)}</option>)}
        </select>
      </label>
    );
    const type = (field: keyof PlmReferenceTypeRow, label: string) => (
      <label className="plm-reftypes-optic">
        <span>{label}</span>
        <input className="sd-input" inputMode="decimal" value={String(r[field])} disabled={viewOnly}
          onChange={(e) => patch(r.key, { [field]: e.target.value } as Partial<PlmReferenceTypeRow>)} />
      </label>
    );
    return (
      <div className="plm-reftypes-optics">
        {pick('opticalCharacter', 'Fiber type', OPTICAL_CHARACTER_OPTIONS)}
        {pick('birefringence', 'Birefringence', BIREFRINGENCE_OPTIONS, birefringenceLabel)}
        {pick('pleochroism', 'Pleochroism', YES_NO)}
        {pick('morphology', 'Morphology', MORPHOLOGY_OPTIONS)}
        {pick('extinction', 'Extinction', EXTINCTION_OPTIONS)}
        {pick('signOfElongation', 'Sign of elongation', ELONGATION_OPTIONS)}
        {type('meanRiAlpha', 'Mean RI α')}
        {type('meanRiGamma', 'Mean RI γ')}
        {r.rangeMode === 'Override' && (
          <label className="plm-reftypes-optic">
            <span>Override source</span>
            <input className="sd-input" value={r.overrideSource} disabled={viewOnly} maxLength={200}
              onChange={(e) => patch(r.key, { overrideSource: e.target.value })} />
          </label>
        )}
      </div>
    );
  };

  return (
    <div className="plm-reftypes">
      <h3 className="sd-section-h">Asbestos types ({rows.length})</h3>
      <AppDataGrid<PlmReferenceTypeRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.key}
        loading={loading}
        showFilters={false}
        hideFooter
        exportFilename="Reference Slide Asbestos Types"
        emptyText="No asbestos — ND reference (blank slide / material)."
        rowDetail={{
          render: opticsPanel,
          expandedIds: optics,
          onExpandedChange: setOptics,
          expandTitle: 'Optics',
        }}
      />
      <div className="plm-reftypes-bar">
        {!viewOnly && (
          <button type="button" className="ca-btn ca-btn--sm" onClick={addType}>
            <span className="tb-i tb-i-green">＋</span> Add asbestos type
          </button>
        )}
        <span className="plm-reftypes-hint">
          Range <b>Rule</b>: {RANGE_RULE_HINT}. <b>Override</b> for a certified range.
        </span>
      </div>
    </div>
  );
}

export default PlmReferenceTypesEditor;
